// Sync select-box lookups from GET /mobile/lookups.json into Redux + IndexedDB.
import axios from "axios";
import _ from "lodash";
import { isOnline } from "./network";
import { getAccessToken } from "modules/auth/token";
import { cacheReplaceSlice, cacheSetUrl, cacheGetLookupsSyncedAt, cacheSetLookupsSyncedAt } from "./cache";

const LOOKUPS_TTL_MS = 15 * 60 * 1000;

const SLICE_ACTIONS = {
  projects: "get_projects",
  project_areas: "get_project_areas",
  project_technical_details_paint_systems: "get_project_technical_details_paint_systems",
  users: "get_user_managements",
  employees: "get_employees",
  project_qa_action_items_statuses: "get_project_qa_action_items_statuses",
  project_qa_welding_inspections_welding_types: "get_project_qa_welding_inspections_welding_types",
  project_qa_welding_inspections_welding_processes: "get_project_qa_welding_inspections_welding_processes",
  project_qa_welding_inspections_welding_stages: "get_project_qa_welding_inspections_welding_stages",
  project_qa_welding_inspections_welding_standards: "get_project_qa_welding_inspections_welding_standards",
  project_qa_welding_inspections_welding_procedures: "get_project_qa_welding_inspections_welding_procedures",
  project_qa_welding_inspections_access_restrictions: "get_project_qa_welding_inspections_access_restrictions",
  project_qa_welding_inspections_viewing_conditions: "get_project_qa_welding_inspections_viewing_conditions",
  project_qa_welding_inspections_surface_conditions: "get_project_qa_welding_inspections_surface_conditions",
  project_qa_welding_inspections_fillet_sizes: "get_project_qa_welding_inspections_fillet_sizes",
  project_qa_welding_inspections_inspection_specs: "get_project_qa_welding_inspections_inspection_specs",
  project_qa_welding_inspections_inspection_criterias: "get_project_qa_welding_inspections_inspection_criterias",
  project_qa_welding_inspections_failure_reasons: "get_project_qa_welding_inspections_failure_reasons",
  workshops: "get_workshops",
  paint_locations: "get_paint_locations",
  store_overalls_statuses: "get_store_overalls_statuses",
  store_overalls_garments: "get_store_overalls_garments",
  address_book_companies: "get_address_book_companies",
  mw_jobs: "GET_MW_JOBS",
  mw_products: "GET_MW_PRODUCTS",
};

const SLICE_URLS = {
  projects: ["/projects.json", "/projects.json?simpleOnly=true"],
  project_areas: ["/projects/all_areas.json"],
  users: ["/user_managements.json", "/user_managements.json?basic=true"],
  employees: ["/employees.json"],
  project_qa_action_items_statuses: ["/project/qa/action_items/statuses.json"],
  project_qa_welding_inspections_welding_types: ["/project/qa/welding_inspections/welding_types.json"],
  project_qa_welding_inspections_welding_processes: ["/project/qa/welding_inspections/welding_processes.json"],
  project_qa_welding_inspections_welding_stages: ["/project/qa/welding_inspections/welding_stages.json"],
  project_qa_welding_inspections_welding_standards: ["/project/qa/welding_inspections/welding_standards.json"],
  project_qa_welding_inspections_welding_procedures: ["/project/qa/welding_inspections/welding_procedures.json"],
  project_qa_welding_inspections_access_restrictions: ["/project/qa/welding_inspections/access_restrictions.json"],
  project_qa_welding_inspections_viewing_conditions: ["/project/qa/welding_inspections/viewing_conditions.json"],
  project_qa_welding_inspections_surface_conditions: ["/project/qa/welding_inspections/surface_conditions.json"],
  project_qa_welding_inspections_fillet_sizes: ["/project/qa/welding_inspections/fillet_sizes.json"],
  project_qa_welding_inspections_inspection_specs: ["/project/qa/welding_inspections/inspection_specs.json"],
  project_qa_welding_inspections_inspection_criterias: ["/project/qa/welding_inspections/inspection_criteria.json"],
  project_qa_welding_inspections_failure_reasons: ["/project/qa/welding_inspections/failure_reasons.json"],
  workshops: ["/workshops.json"],
  paint_locations: ["/paint/locations.json"],
  store_overalls_statuses: ["/store/overalls/statuses.json"],
  store_overalls_garments: ["/store/overalls/garments.json"],
  address_book_companies: ["/address_book/companies.json"],
  mw_jobs: ["/moneyworks/jobs.json"],
  mw_products: ["/moneyworks/products.json"],
};

const SLICE_GROUP_URL = {
  project_areas: (projectId) => `/project/${projectId}/areas.json`,
  project_technical_details_paint_systems: (projectId) => `/project/${projectId}/technical_details/paint_systems.json`,
};

const applyLookupsBundle = async (store, bundle) => {
  if (!bundle || !store) return;

  await Promise.all(
    _.map(SLICE_ACTIONS, async (action, slice) => {
      const rows = bundle[slice];
      if (!_.isArray(rows)) return;

      store.dispatch({
        type: `${action}_FULFILLED`,
        payload: { data: rows },
      });

      await cacheReplaceSlice(slice, rows);
      await Promise.all(_.map(SLICE_URLS[slice], (url) => cacheSetUrl("GET", url, rows)));

      const groupUrl = SLICE_GROUP_URL[slice];
      if (groupUrl) {
        await Promise.all(
          _.map(_.groupBy(rows, "project_id"), (group, projectId) => {
            if (projectId == null || projectId === "undefined") return null;
            return cacheSetUrl("GET", groupUrl(projectId), group);
          })
        );
      }
    })
  );
};

let inflight = null;

export const syncMobileLookups = async (store, { force } = {}) => {
  if (!store || !isOnline() || !getAccessToken()) return;
  if (_.isEmpty(store.getState().current_user)) return;

  if (!force) {
    const syncedAt = await cacheGetLookupsSyncedAt();
    if (syncedAt && Date.now() - syncedAt < LOOKUPS_TTL_MS) return;
  }

  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const { data } = await axios.get("/mobile/lookups.json", {
        skipOffline: true,
        skipOfflineWrite: true,
      });
      await applyLookupsBundle(store, data);
      await cacheSetLookupsSyncedAt(Date.now());
    } catch (_e) {
      // keep whatever is already in the cache
    } finally {
      inflight = null;
    }
  })();

  return inflight;
};

export const preloadMobileLookups = (store) => syncMobileLookups(store, { force: true });
